import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { 
      name: 'Products', 
      href: '#',
      hasDropdown: true,
      dropdownItems: [
        { name: '🤖 AI Solutions', href: '#', isCategory: true },
        { name: 'DB Talker - Business Intelligence', href: '/db-talker' },
        { name: 'DocFlow AI - Document Intelligence', href: '/docflow-ai' },
        { name: 'VisionEdge AI - Video Intelligence', href: '/visionedge-ai' },
        { name: 'InfluenceHub AI - Influencer Marketing', href: '/influencehub-ai' },
        { name: 'divider', href: '#', isDivider: true },
        { name: '⛓️ Blockchain Solutions', href: '#', isCategory: true },
        { name: 'Blockchain Wallet - Digital Infrastructure', href: '/blockchain-wallet' },
        { name: 'divider', href: '#', isDivider: true },
        { name: '📡 IoT Platform', href: '#', isCategory: true },
        { name: 'QuickReach EdgeSphere - IoT Management', href: '/edgesphere' }
      ]
    },
    { name: 'Models by Industry', href: '/models-by-industry' },
    { name: 'FAQ', href: '/faq' },
    { 
      name: 'Resources', 
      href: '#',
      hasDropdown: true,
      dropdownItems: [
        { name: 'ROI Calculator', href: '/roi-calculator' },
        { name: 'Platform Architecture', href: '/architecture' },
        { name: 'AI Evolution', href: '/ai-evolution' },
        { name: 'License', href: '/license' }
      ]
    },
  ];

  return (
    <>
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-white/90 backdrop-blur-sm'
      }`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <span className="text-2xl font-bold text-teal-600">QuickReach</span>
              <span className="bg-gradient-to-r from-primary-800 to-secondary-800 text-white px-3 py-1 rounded-full text-xs font-semibold">
                Private AI
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <div key={item.name} className="relative">
                  {item.hasDropdown ? (
                    <div className="relative">
                      <button
                        onClick={() => setOpenDropdown(openDropdown === item.name ? null : item.name)}
                        className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-primary-800 transition-colors duration-200"
                      >
                        <span>{item.name}</span>
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>
                      
                      {openDropdown === item.name && (
                        <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                          {item.dropdownItems?.map((dropdownItem, idx) => {
                            if (dropdownItem.isDivider) {
                              return <div key={`divider-${idx}`} className="my-2 border-t border-gray-200"></div>;
                            }
                            if (dropdownItem.isCategory) {
                              return (
                                <div key={dropdownItem.name} className="px-4 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                  {dropdownItem.name}
                                </div>
                              );
                            }
                            return (
                              <Link
                                key={dropdownItem.name}
                                to={dropdownItem.href}
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-800 transition-colors duration-200"
                                onClick={() => setOpenDropdown(null)}
                              >
                                {dropdownItem.name}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`text-sm font-medium transition-colors duration-200 relative ${
                        location.pathname === item.href
                          ? 'text-primary-800'
                          : 'text-gray-700 hover:text-primary-800'
                      }`}
                    >
                      {item.name}
                      {location.pathname === item.href && (
                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-800"></div>
                      )}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Book Meeting CTA */}
              <Link
                to="/schedule-meeting"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md"
              >
                Book Meeting
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-gray-700 hover:text-primary-800"
              >
                {isOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.hasDropdown ? (
                      <div>
                        <button
                          onClick={() => setOpenDropdown(openDropdown === item.name ? null : item.name)}
                          className="flex items-center justify-between w-full px-3 py-2 text-base font-medium text-gray-700 hover:text-primary-800 hover:bg-gray-50"
                        >
                          <span>{item.name}</span>
                          <ChevronDownIcon className="w-4 h-4" />
                        </button>
                        {openDropdown === item.name && (
                          <div className="pl-4 space-y-1">
                            {item.dropdownItems?.map((dropdownItem, idx) => {
                              if (dropdownItem.isDivider) {
                                return <div key={`divider-${idx}`} className="my-2 border-t border-gray-200"></div>;
                              }
                              if (dropdownItem.isCategory) {
                                return (
                                  <div key={dropdownItem.name} className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                    {dropdownItem.name}
                                  </div>
                                );
                              }
                              return (
                                <Link
                                  key={dropdownItem.name}
                                  to={dropdownItem.href}
                                  className="block px-3 py-2 text-sm text-gray-600 hover:text-primary-800 hover:bg-gray-50"
                                  onClick={() => {
                                    setOpenDropdown(null);
                                    setIsOpen(false);
                                  }}
                                >
                                  {dropdownItem.name}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block px-3 py-2 text-base font-medium transition-colors duration-200 ${
                          location.pathname === item.href
                            ? 'text-primary-800 bg-primary-50'
                            : 'text-gray-700 hover:text-primary-800 hover:bg-gray-50'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
                
                {/* Mobile Book Meeting CTA */}
                <Link
                  to="/schedule-meeting"
                  className="mx-3 my-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-lg text-base font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md text-center block"
                  onClick={() => setIsOpen(false)}
                >
                  Book Meeting
                </Link>
              </div>
            </div>
          )}
        </nav>
      </header>
      {/* Spacer to prevent content overlap */}
      <div className="h-16"></div>
    </>
  );
};

export default Header;
